/**
 * Normalizes pathname for comparisons (trailing slash, empty).
 * @param {string} pathname
 * @returns {string}
 */
export function normalizePathname(pathname) {
  if (!pathname) return '/';
  const t = pathname.replace(/\/+$/, '');
  return t === '' ? '/' : t;
}

/**
 * Virtual match key: progress URLs under a role-prefixed batch tree.
 * Not a real router path — consumed only by isActiveRoute.
 */
export const CODING_PROGRESS_MATCH = '/coding/progress';

/** Virtual match: faculty coding workspace (overview, batches, assignments, batch detail). */
export const FACULTY_WORKSPACE_MATCH = '/faculty/workspace';

/** Virtual match: faculty workspace batches + batch detail routes. */
export const FACULTY_WORKSPACE_BATCHES_MATCH = '/__nav/faculty/workspace-batches';

/** Virtual match: faculty analytics (overall + platform pages). */
export const FACULTY_ANALYTICS_MATCH = '/faculty/analytics';

/** @deprecated Use {@link FACULTY_ANALYTICS_MATCH}. Kept for legacy redirect active state. */
export const CODING_ANALYTICS_MATCH = '/faculty/coding-analytics';

/** Virtual match: faculty lab review & evaluation surfaces. */
export const FACULTY_REVIEWS_LABS_MATCH = '/__nav/faculty/reviews-labs';

/** Virtual match: faculty project review surfaces. */
export const FACULTY_REVIEWS_PROJECTS_MATCH = '/__nav/faculty/reviews-projects';

/**
 * Virtual match: student assignments, lab dashboard, and IDE (one compact nav row).
 * Not a real router path.
 */
export const STUDENT_CODING_ASSIGNMENTS_MATCH = '/__nav/student/coding-assignments';
export const STUDENT_ASSIGNMENTS_LABS_MATCH = '/__nav/student/assignments-labs';
export const STUDENT_LAB_RESULTS_MATCH = '/__nav/student/lab-results';
export const STUDENT_PROGRESS_MATCH = '/__nav/student/progress';
export const STUDENT_CODING = '/__nav/student/coding/';

/**
 * @param {string} codingHomePath - e.g. '/admin/coding-tracker' or '/faculty/coding-dashboard'
 * @param {string} currentPath
 */
function isCodingHomeActive(codingHomePath, currentPath) {
  const cur = normalizePathname(currentPath);
  const nav = normalizePathname(codingHomePath);
  const m = nav.match(/^(.+)\/(coding-tracker|coding-dashboard)$/);
  if (!m) return false;
  const root = m[1];
  if (cur === nav || cur.startsWith(`${nav}/`)) return true;
  if (root && cur.startsWith(`${root}/batch/`) && !cur.includes('/progress')) return true;
  return false;
}

/**
 * Returns whether a nav item should show as active for the current location.
 *
 * @param {string} path - Nav item route key: real path or {@link CODING_PROGRESS_MATCH}
 * @param {string} currentPath - `location.pathname`
 * @returns {boolean}
 */
export function isActiveRoute(path, currentPath) {
  const cur = normalizePathname(currentPath);
  const nav = normalizePathname(path);

  if (nav === CODING_PROGRESS_MATCH) {
    return (
      cur.includes('/progress') &&
      (cur.startsWith('/admin/batch') || cur.startsWith('/faculty/batch'))
    );
  }

  if (nav === FACULTY_WORKSPACE_MATCH) {
    return cur === nav || cur === `${nav}/overview`;
  }

  if (nav === FACULTY_WORKSPACE_BATCHES_MATCH) {
    return (
      cur.startsWith('/faculty/workspace/batches') ||
      (cur.startsWith('/faculty/batch/') && !cur.includes('/progress'))
    );
  }

  if (nav === FACULTY_ANALYTICS_MATCH) {
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav === CODING_ANALYTICS_MATCH) {
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav === FACULTY_REVIEWS_LABS_MATCH) {
    return (
      cur.startsWith('/faculty/lab-results') ||
      cur.startsWith('/faculty/lab-internals')
    );
  }

  if (nav === FACULTY_REVIEWS_PROJECTS_MATCH) {
    return cur.startsWith('/faculty/projects') || cur.startsWith('/faculty/project/');
  }

  if (nav === STUDENT_CODING_ASSIGNMENTS_MATCH) {
    return cur.startsWith('/student/assignments');
  }

  if (nav === STUDENT_ASSIGNMENTS_LABS_MATCH) {
    return (
      cur.startsWith('/student/lab-internals') ||
      cur.startsWith('/student/ide/')
    );
  }

  if (nav === STUDENT_LAB_RESULTS_MATCH) {
    return (
      cur.startsWith('/student/lab-results') ||
      cur.startsWith('/student/results')
    );
  }

  if (nav === STUDENT_PROGRESS_MATCH) {
    return cur.startsWith('/student/progress') || cur.startsWith('/student/coding/progress');
  }

  if (/\/coding-tracker$/.test(nav) || /\/coding-dashboard$/.test(nav)) {
    return (
      isCodingHomeActive(nav, currentPath) ||
      cur.startsWith('/faculty/workspace/batches')
    );
  }

  // Problem Library is the parent of its own sibling nav item: the Analytics
  // page lives at `<library>/analytics`, so a plain prefix match lights up BOTH
  // rows at once. Detail pages (`<library>/:referenceKey`) must still highlight
  // the library, so this is a targeted exclusion rather than an exact match.
  if (/^\/(admin|faculty)\/problem-library$/.test(nav)) {
    if (cur === nav) return true;
    if (!cur.startsWith(`${nav}/`)) return false;
    return !cur.startsWith(`${nav}/analytics`);
  }

  if (nav.startsWith('/admin/')) {
    // Prepare workspace prefixes mock-interview preparation siblings
    // (campaigns / students / insights / interviews) — exact + journey only.
    if (nav === '/admin/placement/preparation') {
      return cur === nav || cur.startsWith('/admin/placement/journey/');
    }
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav.startsWith('/faculty/')) {
    if (nav === '/faculty/workspace/overview') {
      return cur === '/faculty/workspace' || cur === nav;
    }
    if (nav === '/faculty/placement/preparation') {
      return cur === nav || cur.startsWith('/faculty/placement/journey/');
    }
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav.startsWith('/student/')) {
    // Hub root prefixes every placement route — exact match only so child pages
    // (Mock Interviews, Profile, …) do not also highlight Readiness.
    if (nav === '/student/placement') {
      return cur === '/student/placement';
    }
    return cur === nav || cur.startsWith(`${nav}/`);
  }

  if (nav.startsWith('/plagiarism-analytics')) {
    return cur === '/plagiarism-analytics' || cur.startsWith('/plagiarism-analytics/');
  }

  if (nav.startsWith('/assignments')) {
    return cur === '/assignments' || cur.startsWith('/assignments/');
  }

  return cur === nav || cur.startsWith(`${nav}/`);
}

/**
 * Active state for a nav item: uses optional `matchPath` when the link `path` differs from match semantics.
 * @param {{ path: string, matchPath?: string }} item
 * @param {string} pathname
 */
export function isNavItemActive(item, pathname) {
  return isActiveRoute(item.matchPath ?? item.path, pathname);
}
